import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tooltip } from "../ui/tooltip";
import {
  PlayIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  LoaderIcon,
  DatabaseIcon,
  ExternalLinkIcon,
  Save,
  Zap,
  HelpCircle,
  EditIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { usePermissions } from "../../PermissionsProvider";
import { ConversationBuilderService } from "../../services/ConversationBuilderService";
import { FinetuneRunService } from "../../services/FinetuneRunService";
import { TestSetService } from "../../services/TestSetService";

// Timing constants
const TRIGGER_GUARD_WINDOW_MS = 10000; // 10 seconds
const POLL_INTERVAL_RUNNING_MS = 10000; // 10 seconds
const EXECUTION_COMPLETE_CLEAR_DELAY_MS = 5000; // 5 seconds

// Timeline step types for conversation tree execution
type ExecutionStep =
  | "READY"
  | "BUILDING_RESOURCES"
  | "RESOURCES_COMPLETE"
  | "FINETUNING"
  | "COMPLETE"
  | "FAILED";
type TriggerGuard = { startedAt: number; preStep: ExecutionStep } | null;

// Execution state for persistence
interface ExecutionState {
  step: ExecutionStep;
  details: ExecutionDetails;
  lastSuccessfulStep?: string;
  failedStep?: string;
  triggerGuard?: { startedAt: number; preStep: ExecutionStep } | null;
}

interface TimelineStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  tooltip: string;
}

interface ExecutionDetails {
  compositionId?: number;
  compositionName?: string;
  testSetId?: number;
  testSetName?: string;
  finetuneModelId?: number;
  finetuneModelName?: string;
}

// Define timeline steps - matching ConfigTab exactly
const timelineSteps: TimelineStep[] = [
  {
    id: "save",
    title: "Save",
    description: "Tree saved and ready",
    icon: Save,
    tooltip:
      "Create and save your conversation tree structure with all nodes, decision logic, and flow connections.",
  },
  {
    id: "generate-data",
    title: "Generate Data",
    description: "Creating training data",
    icon: DatabaseIcon,
    tooltip:
      "Automatically create diverse training conversations that follow your tree structure.",
  },
  {
    id: "finetune",
    title: "Finetune Run",
    description: "Training the model",
    icon: Zap,
    tooltip:
      "Use the generated training data to fine-tune a language model specifically for your conversation patterns.",
  },
];

// removed unused styling helper

// Helper to get step index for progress calculation
const getStepIndex = (stepId: string): number => {
  const stepMap: Record<string, number> = {
    save: 0,
    "generate-data": 1,
    finetune: 2,
  };
  return stepMap[stepId] || 0;
};

// Helper to get current step ID from execution step
const getCurrentStepId = (step: ExecutionStep): string => {
  switch (step) {
    case "READY":
      return "save";
    case "BUILDING_RESOURCES":
      return "generate-data";
    case "RESOURCES_COMPLETE":
      return "finetune";
    case "FINETUNING":
      return "finetune";
    case "COMPLETE":
      return "finetune";
    case "FAILED":
      return "save"; // Default fallback
    default:
      return "save";
  }
};

const ConversationTreeExecution: React.FC = () => {
  const { treeId } = useParams<{ treeId: string }>();
  const navigate = useNavigate();
  const { apiKey, companyId } = usePermissions();

  // State management
  const [executionStep, setExecutionStep] = useState<ExecutionStep>("READY");
  const [executionDetails, setExecutionDetails] = useState<ExecutionDetails>(
    {}
  );
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  const [treeInfo, setTreeInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastSuccessfulStep, setLastSuccessfulStep] = useState<string>("save");
  const [failedStep, setFailedStep] = useState<string | null>(null);
  const [triggerGuard, setTriggerGuard] = useState<TriggerGuard>(null);
  const [finetuneRunStatus, setFinetuneRunStatus] = useState<string | null>(null);

  // Persistence helpers
  const getStorageKey = (treeId: string) => `execution-state-${treeId}`;

  const saveExecutionState = (state: ExecutionState) => {
    if (treeId) {
      localStorage.setItem(getStorageKey(treeId), JSON.stringify(state));
    }
  };

  const loadExecutionState = (): ExecutionState | null => {
    if (!treeId) return null;
    try {
      const saved = localStorage.getItem(getStorageKey(treeId));
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const clearExecutionState = () => {
    if (treeId) {
      localStorage.removeItem(getStorageKey(treeId));
    }
  };

  // Load tree information and restore state on mount
  useEffect(() => {
    if (!treeId || !apiKey || !companyId) return;

    const sub = ConversationBuilderService.listLatestConversationTreesByCompany(
      companyId,
      apiKey
    ).subscribe(
      (response: any[]) => {
        const tree = response.find((t: any) => t.id.toString() === treeId);
        if (tree) {
          setTreeInfo(tree);
          // Restore execution state if it exists
          const savedState = loadExecutionState();
          if (savedState) {
            setExecutionStep(savedState.step);
            setExecutionDetails(savedState.details);
            setLastSuccessfulStep(savedState.lastSuccessfulStep || "save");
            setFailedStep(savedState.failedStep || null);
            if (savedState.triggerGuard && savedState.triggerGuard.startedAt) {
              const withinWindow =
                Date.now() - savedState.triggerGuard.startedAt <
                TRIGGER_GUARD_WINDOW_MS;
              setTriggerGuard(withinWindow ? savedState.triggerGuard : null);
            }
          }
        } else {
          toast.error("Conversation tree not found");
          navigate("/forge/conversation-trees");
        }
        setLoading(false);
      },
      (error: any) => {
        console.error("Error loading tree info:", error);
        toast.error("Failed to load conversation tree");
        setLoading(false);
      }
    );

    return () => {
      if (sub) sub.unsubscribe();
    };
  }, [treeId, apiKey, companyId, navigate]);

  // Save state whenever it changes
  useEffect(() => {
    if (treeId) {
      const state: ExecutionState = {
        step: executionStep,
        details: executionDetails,
        lastSuccessfulStep,
        failedStep,
        triggerGuard,
      };
      saveExecutionState(state);
    }
  }, [
    executionStep,
    executionDetails,
    lastSuccessfulStep,
    failedStep,
    triggerGuard,
    treeId,
  ]);

  // Clear execution state when completed
  useEffect(() => {
    if (executionStep === "COMPLETE") {
      // Clear state after a delay so user can see the completion
      const timer = setTimeout(() => {
        clearExecutionState();
      }, EXECUTION_COMPLETE_CLEAR_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [executionStep]);

  // Helper function to determine if execution is currently running
  const isExecutionRunning = () => {
    return (
      executionStep === "BUILDING_RESOURCES" || executionStep === "FINETUNING"
    );
  };

  // Helper function to convert backend status to execution step
  const getExecutionStepFromStatus = (status: string): ExecutionStep => {
    switch (status?.toUpperCase()) {
      case "SAVED":
        return "READY";
      case "BUILDING_RESOURCES":
        // Generate Data is running
        return "BUILDING_RESOURCES";
      case "READY_FOR_FINE_TUNE":
        // Generate Data completed, ready for finetuning
        return "RESOURCES_COMPLETE";
      case "FINETUNING":
        // Finetune is running
        return "FINETUNING";
      case "COMPLETED":
        // Finetune completed
        return "COMPLETE";
      case "FAILED":
        return "FAILED";
      default:
        return "READY";
    }
  };

  // Function to fetch current execution status
  const fetchExecutionStatus = useCallback(() => {
    if (!treeId || !apiKey) return;

    ConversationBuilderService.getConversationTreeExecutionStatus(
      treeId,
      apiKey
    ).subscribe(
      (response: any) => {
        if (response) {
          const newExecutionStep = getExecutionStepFromStatus(response.status);
          if (triggerGuard) {
            const withinGuardWindow =
              Date.now() - triggerGuard.startedAt < TRIGGER_GUARD_WINDOW_MS; // 10s
            const isPreOrReady =
              newExecutionStep === triggerGuard.preStep ||
              newExecutionStep === "READY";
            if (withinGuardWindow && isPreOrReady) {
              return; // ignore stale status
            }
            if (!withinGuardWindow || !isPreOrReady) {
              setTriggerGuard(null);
            }
          }

          if (newExecutionStep !== executionStep) {
            setExecutionStep(newExecutionStep);
          }

          if (newExecutionStep === "BUILDING_RESOURCES") {
            setLastSuccessfulStep("save");
            setFailedStep(null);
          } else if (newExecutionStep === "RESOURCES_COMPLETE") {
            setLastSuccessfulStep("generate-data");
            setFailedStep(null);
          } else if (newExecutionStep === "FINETUNING") {
            setLastSuccessfulStep("generate-data");
            setFailedStep(null);
          } else if (newExecutionStep === "COMPLETE") {
            setLastSuccessfulStep("finetune");
            setFailedStep(null);
          } else if (newExecutionStep === "FAILED") {
            if (response.metadata?.finetune_run_id) {
              setFailedStep("finetune");
              setLastSuccessfulStep("generate-data");
            } else if (response.metadata?.data_composition_id) {
              setFailedStep("finetune");
              setLastSuccessfulStep("generate-data");
            } else {
              setFailedStep("generate-data");
              setLastSuccessfulStep("save");
            }
          }

          if (response.metadata) {
            const newDetails: any = {};
            if (response.metadata.data_composition_id) {
              newDetails.compositionId = response.metadata.data_composition_id;
              newDetails.compositionName =
                response.metadata.composition_name ||
                `Composition ${response.metadata.data_composition_id}`;
            }
            if (response.metadata.test_set_id) {
              newDetails.testSetId = response.metadata.test_set_id;
              newDetails.testSetName =
                response.metadata.test_set_name ||
                `Test Set ${response.metadata.test_set_id}`;
            }
            if (response.metadata.finetune_run_id) {
              newDetails.finetuneModelId = response.metadata.finetune_run_id;
              newDetails.finetuneModelName =
                response.metadata.finetune_run_name ||
                `Run ${response.metadata.finetune_run_id}`;
            }
            if (Object.keys(newDetails).length > 0) {
              setExecutionDetails((prev) => ({ ...prev, ...newDetails }));
            }
          }
        }
      },
      (error: any) => {
        console.error("Error fetching execution status:", error);
      }
    );
  }, [treeId, apiKey, executionStep, triggerGuard]);

  // Poll only while running (every 10s); when not running, no polling
  useEffect(() => {
    if (!treeId || !apiKey) return;

    if (!isExecutionRunning()) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchExecutionStatus();
    }, POLL_INTERVAL_RUNNING_MS);

    return () => clearInterval(intervalId);
  }, [fetchExecutionStatus, executionStep, triggerGuard]);

  // Initial fetch when component mounts (single fetch when idle)
  useEffect(() => {
    if (treeId && apiKey && treeInfo) {
      fetchExecutionStatus();
    }
  }, [treeId, apiKey, treeInfo, fetchExecutionStatus, triggerGuard]);

  // Fetch finetune run status when available
  useEffect(() => {
    if (executionDetails.finetuneModelId && apiKey) {
      FinetuneRunService.getFinetuneRunById(
        executionDetails.finetuneModelId,
        undefined,
        apiKey
      ).subscribe(
        (run: any) => {
          if (run && run.status) {
            setFinetuneRunStatus(run.status);
          } else {
            setFinetuneRunStatus(null);
          }
        },
        () => setFinetuneRunStatus(null)
      );
    }
  }, [executionDetails.finetuneModelId, apiKey]);

  // Refresh when the tab becomes visible
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchExecutionStatus();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [fetchExecutionStatus]);

  // Handle build model execution - now just triggers the process, polling handles updates
  const handleBuildModel = () => {
    if (!treeInfo || !apiKey || !companyId) return;

    setFailedStep(null);
    // Immediately reflect running state so it persists across refresh/navigation
    const preStep: ExecutionStep = executionStep;
    setTriggerGuard({ startedAt: Date.now(), preStep });
    setExecutionStep("BUILDING_RESOURCES");
    setLastSuccessfulStep("save");

    // Trigger the full finetune process (which includes resources + finetune)
    toast.info("Starting execution...");
    ConversationBuilderService.createConversationTreeFinetune(
      companyId,
      treeInfo.application_name,
      treeInfo.intent_name,
      treeInfo.conversation_flow_name,
      treeInfo.id.toString(),
      apiKey
    ).subscribe(
      () => {
        toast.success("Execution started! Status will update automatically.");
      },
      (error: any) => {
        console.error("Error starting execution:", error);
        setExecutionStep("FAILED");
        toast.error("Failed to start execution. Please try again.");
      }
    );
  };

  const currentStepId = getCurrentStepId(executionStep);

  // Calculate progress width based on last successful step
  const getProgressWidth = () => {
    const lastSuccessfulIndex = getStepIndex(lastSuccessfulStep);
    const totalSteps = timelineSteps.length - 1; // 0-based index
    return (lastSuccessfulIndex / totalSteps) * 100;
  };

  const progressWidth = getProgressWidth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <div className="flex items-center justify-center py-24">
            <LoaderIcon className="w-8 h-8 animate-spin text-maitai-green" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/forge/conversation-trees")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Conversation Trees
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Execute</span>
                </li>
              </ol>
            </nav>

            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                    {treeInfo?.conversation_flow_name}
                  </h1>
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-muted text-foreground font-medium text-xs">
                    v{treeInfo?.conversation_flow_version}
                  </span>
                </div>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  {treeInfo?.application_name} •{" "}
                  {treeInfo?.intent_name || "dev1"}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Run Button */}
                <Button
                  onClick={handleBuildModel}
                  disabled={
                    isExecutionRunning() || executionStep === "COMPLETE"
                  }
                  className={cn(
                    "gap-2 px-6 py-3",
                    executionStep === "COMPLETE"
                      ? "bg-maitai-green text-white hover:bg-maitai-green/90"
                      : "bg-primary text-primary-foreground hover:bg-primary/90"
                  )}>
                  {isExecutionRunning() ? (
                    <>
                      <LoaderIcon className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : executionStep === "COMPLETE" ? (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      Finetune Triggered
                    </>
                  ) : (
                    <>
                      <PlayIcon className="w-4 h-4" />
                      Run
                    </>
                  )}
                </Button>

                {/* Edit Button to open Builder */}
                <Button
                  onClick={() =>
                    navigate(`/forge/conversation-tree?id=${treeId}`)
                  }
                  className="gap-2 px-6 py-3 border border-border bg-background text-foreground hover:bg-accent"
                  disabled={!treeId}>
                  <EditIcon className="w-4 h-4" />
                  Edit
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-8">
          {/* Progress Card */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-gc-med text-foreground">
                Execution Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              {/* Progress Timeline */}
              <div className="relative h-20 overflow-visible">
                {/* Timeline base line */}
                <div className="absolute top-4 left-[10%] right-[10%] h-0.5 bg-muted-foreground/20"></div>

                {/* Progress line */}
                <div
                  className={cn(
                    "absolute top-4 left-[10%] h-0.5 transition-all duration-500 bg-maitai-green"
                  )}
                  style={{ width: `${progressWidth * 0.8}%` }}></div>

                {/* Timeline steps */}
                {timelineSteps.map((step, index) => {
                  const isCompleted = getStepIndex(lastSuccessfulStep) >= index;
                  const isCurrent = currentStepId === step.id;
                  const isStepFailed = failedStep === step.id;
                  const isRunning =
                    (executionStep === "BUILDING_RESOURCES" &&
                      step.id === "generate-data") ||
                    (executionStep === "FINETUNING" && step.id === "finetune");
                  const Icon = step.icon;

                  const leftPosition =
                    index === 0 ? "10%" : index === 1 ? "50%" : "90%";
                  const tooltipSide =
                    index === 0 ? "right" : index === 2 ? "left" : "top";
                  const stepZIndex = hoveredTooltip === step.id ? 70 : 20;

                  return (
                    <div
                      key={step.id}
                      className="absolute flex flex-col items-center transform -translate-x-1/2 group"
                      style={{ left: leftPosition, zIndex: stepZIndex }}>
                      {/* Step circle */}
                      <div
                        className={cn(
                          "w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300",
                          isStepFailed
                            ? "bg-destructive border-destructive text-white"
                            : isCompleted
                            ? "bg-maitai-green border-maitai-green text-white shadow-sm"
                            : isCurrent
                            ? "bg-background border-maitai-green text-maitai-green shadow-sm"
                            : "bg-background border-muted-foreground/30 text-muted-foreground"
                        )}>
                        {isStepFailed ? (
                          <AlertCircleIcon className="w-4 h-4" />
                        ) : isRunning ? (
                          <LoaderIcon className="w-4 h-4 animate-spin" />
                        ) : isCompleted ? (
                          <CheckCircleIcon className="w-4 h-4" />
                        ) : (
                          <Icon className="w-4 h-4" />
                        )}
                      </div>

                      {/* Step content */}
                      <div className="mt-1 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <h3
                            className={cn(
                              "text-sm font-gc-med",
                              isStepFailed
                                ? "text-destructive"
                                : isCompleted
                                ? "text-maitai-green"
                                : isCurrent
                                ? "text-maitai-green"
                                : "text-muted-foreground"
                            )}>
                            {step.title}
                          </h3>
                          <div
                            className="relative z-30"
                            onMouseEnter={() => setHoveredTooltip(step.id)}
                            onMouseLeave={() => setHoveredTooltip(null)}>
                            <Tooltip
                              content={step.tooltip}
                              side={tooltipSide as any}>
                              <div className="inline-flex items-center justify-center">
                                <HelpCircle className="w-2.5 h-2.5 text-muted-foreground hover:text-foreground cursor-help transition-colors" />
                              </div>
                            </Tooltip>
                          </div>
                        </div>
                        {/* Running status on separate line */}
                        {isRunning && (
                          <div className="text-xs text-muted-foreground mt-2">
                            (Running)
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Status Message - only show when not in READY state */}
              {executionStep !== "READY" && (
                <div className="pt-8 border-t border-border mt-6">
                  <div
                    className={cn(
                      "p-4 rounded-lg text-sm",
                      executionStep === "FAILED"
                        ? "bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400"
                        : executionStep === "COMPLETE"
                        ? "bg-maitai-green/10 text-maitai-green"
                        : "bg-muted text-muted-foreground"
                    )}>
                    {executionStep === "BUILDING_RESOURCES" &&
                      "Building data composition and test set..."}
                    {executionStep === "RESOURCES_COMPLETE" &&
                      "Resources built successfully. Ready to fine-tune model."}
                    {executionStep === "FINETUNING" &&
                      "Fine-tuning model with generated resources..."}
                    {executionStep === "COMPLETE" && "Finetune Triggered"}
                    {executionStep === "FAILED" &&
                      "Execution failed. Please check the logs and try again."}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Details Card */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-gc-med text-foreground">
                Execution Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Tree Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-gc-med text-foreground">
                  Tree Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <div className="font-gc-med">
                      {treeInfo?.conversation_flow_name}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Version:</span>
                    <div className="font-gc-med">
                      v{treeInfo?.conversation_flow_version}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Application:</span>
                    <div className="font-gc-med">
                      {treeInfo?.application_name}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Intent:</span>
                    <div className="font-gc-med">{treeInfo?.intent_name}</div>
                  </div>
                </div>
              </div>

              {/* Generated Resources */}
              {(executionDetails.compositionId ||
                executionDetails.testSetId ||
                executionDetails.finetuneModelId) && (
                <div className="space-y-2 pt-3 border-t border-border text-sm">
                  <h3 className="text-lg font-gc-med text-foreground">
                    Generated Resources
                  </h3>

                  {/* Data Composition */}
                  {executionDetails.compositionId && (
                    <div className="flex items-center justify-between py-2 px-0 bg-muted/50 rounded-md">
                      <div>
                        <div className="font-gc-med text-foreground">Data Composition</div>
                        <div className="text-sm text-muted-foreground">{executionDetails.compositionName || `Composition ${executionDetails.compositionId}`}</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/forge/compositions/${executionDetails.compositionId}`
                          )
                        }
                        className="gap-2">
                        View <ExternalLinkIcon className="w-3 h-3" />
                      </Button>
                    </div>
                  )}

                  {/* Test Set */}
                  {executionDetails.testSetId && (
                    <div className="flex items-center justify-between py-2 px-0 bg-muted/50 rounded-md">
                      <div>
                        <div className="font-gc-med text-foreground">Test Set</div>
                        <div className="text-sm text-muted-foreground">{executionDetails.testSetName || `Test Set ${executionDetails.testSetId}`}</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const id = executionDetails.testSetId as number;
                          TestSetService.getTestSet(
                            String(id),
                            undefined,
                            apiKey
                          ).subscribe(
                            (data: any) => {
                              if (data && data.intent_group_id) {
                                navigate(
                                  `/intent-group/${data.intent_group_id}/testsets/${id}`
                                );
                              }
                            },
                            () => {
                              /* silently ignore */
                            }
                          );
                        }}
                        className="gap-2">
                        View <ExternalLinkIcon className="w-3 h-3" />
                      </Button>
                    </div>
                  )}

                  {/* Finetune Run */}
                  {executionDetails.finetuneModelId && (
                    <div className="flex items-center justify-between py-2 px-0 bg-muted/50 rounded-md">
                      <div>
                        <div className="font-gc-med text-foreground flex items-center gap-2">
                          <span>Finetune Run</span>
                          {finetuneRunStatus && <Badge variant="outline">{finetuneRunStatus}</Badge>}
                        </div>
                        <div className="text-sm text-muted-foreground">{executionDetails.finetuneModelName || `Run ${executionDetails.finetuneModelId}`}</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/forge/finetuning/${executionDetails.finetuneModelId}`
                          )
                        }
                        className="gap-2">
                        View <ExternalLinkIcon className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ConversationTreeExecution;
